import React from 'react';
import {Stack, Group, TextInput, ActionIcon, Button, Text, Menu, Box} from '@mantine/core';
import {IconTrash, IconPlus, IconGripVertical} from '@tabler/icons-react';
import {
    DndContext,
    closestCenter,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
    DragEndEvent,
} from '@dnd-kit/core';
import {
    arrayMove,
    SortableContext,
    sortableKeyboardCoordinates,
    useSortable,
    verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import {CSS} from '@dnd-kit/utilities';
import {SocialLink, PlatformType} from '@/types/social_links';
import {getPlatformIcon, generateSocialLinkId, extractUsername, buildSocialUrl} from '@/utils/social_links_helpers';

type SocialLinksEditorProps = {
    links: SocialLink[];
    onChange: (links: SocialLink[]) => void;
    maxLinks?: number;
};

const platformOptions: {value: PlatformType; label: string}[] = [
    {value: 'instagram', label: 'Instagram'},
    {value: 'bandcamp', label: 'Bandcamp'},
    {value: 'facebook', label: 'Facebook'},
    {value: 'soundcloud', label: 'SoundCloud'},
    {value: 'spotify', label: 'Spotify'},
    {value: 'twitter', label: 'Twitter'},
    {value: 'custom', label: 'Custom Link'},
];

type LinkItemProps = {
    link: SocialLink;
    onUpdate: (id: string, value: string) => void;
    onUpdateType: (id: string, type: PlatformType) => void;
    onDelete: (id: string) => void;
};

function LinkItem({link, onUpdate, onUpdateType, onDelete}: LinkItemProps) {
    const Icon = getPlatformIcon(link.type);

    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging,
    } = useSortable({id: link.id});

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        onUpdate(link.id, e.target.value);
    };

    return (
        <Box ref={setNodeRef} style={style}>
            <Group gap="xs" wrap="nowrap">
                <ActionIcon
                    variant="subtle"
                    color="gray"
                    {...attributes}
                    {...listeners}
                    style={{cursor: 'grab', touchAction: 'none'}}
                >
                    <IconGripVertical size={16} />
                </ActionIcon>

                <Menu position="bottom-start" withinPortal zIndex={10000}>
                    <Menu.Target>
                        <ActionIcon
                            variant="subtle"
                            size="lg"
                            style={{cursor: 'pointer'}}
                        >
                            <Icon size={20} />
                        </ActionIcon>
                    </Menu.Target>
                    <Menu.Dropdown>
                        {platformOptions.map((option) => {
                            const OptionIcon = getPlatformIcon(option.value);
                            return (
                                <Menu.Item
                                    key={option.value}
                                    leftSection={<OptionIcon size={18} />}
                                    onClick={() => onUpdateType(link.id, option.value)}
                                >
                                    {option.label}
                                </Menu.Item>
                            );
                        })}
                    </Menu.Dropdown>
                </Menu>

                <TextInput
                    key={`${link.id}-${link.type}`}
                    size="xs"
                    placeholder=""
                    defaultValue={link.url ? extractUsername(buildSocialUrl(link.url, link.type), link.type) : ''}
                    onChange={handleChange}
                    style={{flex: 1}}
                />

                <ActionIcon
                    variant="subtle"
                    onClick={() => onDelete(link.id)}
                >
                    <IconTrash size={16} />
                </ActionIcon>
            </Group>
        </Box>
    );
}

export function SocialLinksEditor({links, onChange, maxLinks = 15}: SocialLinksEditorProps) {
    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: {
                delay: 100,
                tolerance: 5,
            },
        }),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        })
    );

    const handleDragEnd = (event: DragEndEvent) => {
        const {active, over} = event;

        if (over && active.id !== over.id) {
            const oldIndex = links.findIndex((link) => link.id === active.id);
            const newIndex = links.findIndex((link) => link.id === over.id);

            const reorderedLinks = arrayMove(links, oldIndex, newIndex).map((link, index) => ({
                ...link,
                order: index,
            }));

            onChange(reorderedLinks);
        }
    };

    const handleUpdate = (id: string, url: string) => {
        const updatedLinks = links.map((link) =>
            link.id === id ? {...link, url} : link
        );
        onChange(updatedLinks);
    };

    const handleUpdateType = (id: string, type: PlatformType) => {
        const updatedLinks = links.map((link) =>
            link.id === id ? {...link, type} : link
        );
        onChange(updatedLinks);
    };

    const handleDelete = (id: string) => {
        const filteredLinks = links.filter((link) => link.id !== id);
        const reorderedLinks = filteredLinks.map((link, index) => ({
            ...link,
            order: index,
        }));
        onChange(reorderedLinks);
    };

    const handleAdd = () => {
        const newLink: SocialLink = {
            id: generateSocialLinkId(),
            type: 'instagram',
            url: '',
            order: links.length,
        };
        onChange([...links, newLink]);
    };

    const canAddMore = links.length < maxLinks;

    return (
        <Stack gap="xs">
            <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
                autoScroll={false}
            >
                <SortableContext
                    items={links.map((link) => link.id)}
                    strategy={verticalListSortingStrategy}
                >
                    {links.map((link) => (
                        <LinkItem
                            key={link.id}
                            link={link}
                            onUpdate={handleUpdate}
                            onUpdateType={handleUpdateType}
                            onDelete={handleDelete}
                        />
                    ))}
                </SortableContext>
            </DndContext>

            <Group justify="space-between">
                <Button
                    size="xs"
                    variant="light"
                    leftSection={<IconPlus size={14} />}
                    onClick={handleAdd}
                    disabled={!canAddMore}
                >
                    Add Link
                </Button>
                {!canAddMore && (
                    <Text size="xs" c="dimmed">
                        Maximum {maxLinks} links
                    </Text>
                )}
            </Group>
        </Stack>
    );
}
